// ─────────────────────────────────────────────
// KLOTHIQ — Adjust Stock Modal (Simplified UX)
// TASK-FE-STOCK-001: Add / Remove toggle replaces 5-option dropdown.
// Advanced modes (Damaged, Set Exact, Opening Stock) collapsed by default.
// All existing RPC calls are preserved unchanged.
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input } from '@/components/ui/FormControls';
import { useState, useId } from 'react';
import { useAddOpeningStock, useAdjustStock } from '@/hooks/useInventory';
import { toast } from 'sonner';
import {
  Loader2, X, PackageCheck, AlertCircle,
  TrendingDown, TrendingUp, ChevronDown, ChevronUp,
  Plus, Minus,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AdjustStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  variantId: string;
  sku: string;
  productName: string;
  currentOnHand?: number;
  unitCost?: number;
}

type AdjustmentMode = 'set_exact' | 'adjustment_out' | 'damaged' | 'adjustment_in' | 'opening_stock';

export default function AdjustStockModal({
  isOpen,
  onClose,
  variantId,
  sku,
  productName,
  currentOnHand = 0,
  unitCost = 0,
}: AdjustStockModalProps) {
  const addOpeningStock = useAddOpeningStock();
  const adjustStock = useAdjustStock();

  const titleId = useId();
  const descId = useId();

  // ── Primary UX state: simple Add / Remove toggle ───────────────
  const [primaryMode, setPrimaryMode] = useState<'add' | 'remove'>('add');

  // ── Advanced section ───────────────────────────────────────────
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedMode, setAdvancedMode] = useState<'set_exact' | 'damaged' | 'opening_stock' | null>(null);

  // ── Field state ────────────────────────────────────────────────
  const [quantity, setQuantity] = useState<string>('');
  const [targetStock, setTargetStock] = useState<string>('');
  const [cost, setCost] = useState<string>(unitCost > 0 ? unitCost.toString() : '');
  const [reason, setReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  // ── Derive the effective movement type ────────────────────────
  const effectiveMode: AdjustmentMode = (() => {
    if (showAdvanced && advancedMode) return advancedMode;
    return primaryMode === 'add' ? 'adjustment_in' : 'adjustment_out';
  })();

  // ── Live calculation ───────────────────────────────────────────
  const parsedQty = parseInt(quantity, 10);
  const isQtyValid = !isNaN(parsedQty) && parsedQty > 0;

  const parsedTarget = parseInt(targetStock, 10);
  const isTargetValid = !isNaN(parsedTarget) && parsedTarget >= 0;
  const targetDelta = isTargetValid ? parsedTarget - currentOnHand : 0;

  let computedResultingStock = currentOnHand;
  let computedDelta = 0;
  let isDecrease = false;
  let isIncrease = false;
  let isNoChange = false;

  if (effectiveMode === 'set_exact') {
    computedResultingStock = isTargetValid ? parsedTarget : currentOnHand;
    computedDelta = targetDelta;
    isDecrease = targetDelta < 0;
    isIncrease = targetDelta > 0;
    isNoChange = targetDelta === 0;
  } else if (effectiveMode === 'adjustment_out' || effectiveMode === 'damaged') {
    computedDelta = isQtyValid ? -parsedQty : 0;
    computedResultingStock = currentOnHand - (isQtyValid ? parsedQty : 0);
    isDecrease = true;
  } else {
    // adjustment_in or opening_stock
    computedDelta = isQtyValid ? parsedQty : 0;
    computedResultingStock = currentOnHand + (isQtyValid ? parsedQty : 0);
    isIncrease = true;
  }

  // ── Inline validation for remove ──────────────────────────────
  const isOverRemoval =
    (effectiveMode === 'adjustment_out' || effectiveMode === 'damaged') &&
    isQtyValid &&
    parsedQty > currentOnHand;

  // ── Confirm button enabled logic ──────────────────────────────
  const canSubmit = (() => {
    if (isSubmitting) return false;
    if (isOverRemoval) return false;
    if (effectiveMode === 'set_exact') return isTargetValid && !isNoChange;
    return isQtyValid;
  })();

  // ── Submit handler ─────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (effectiveMode === 'set_exact') {
      if (!isTargetValid) {
        toast.error('Target stock must be a non-negative integer (0 or greater)');
        return;
      }
      if (targetDelta === 0) {
        toast.error(`Stock is already at ${currentOnHand}. No change needed.`);
        return;
      }
      if (!reason.trim()) {
        toast.error('Reason is required for stock adjustments');
        return;
      }

      setIsSubmitting(true);
      try {
        const actualMovementType = targetDelta < 0 ? 'adjustment_out' : 'adjustment_in';
        await adjustStock.mutateAsync({
          variant_id: variantId,
          quantity: Math.abs(targetDelta),
          movement_type: actualMovementType,
          reason: reason.trim(),
        });
        toast.success(`Stock corrected for ${sku}: ${currentOnHand} → ${parsedTarget}`);
        onClose();
      } catch (err: unknown) {
        console.error(err);
        toast.error(err instanceof Error ? err.message : 'Failed to adjust stock');
        // Do NOT close — preserve quantity and keep modal open (FR-STOCK-005)
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (effectiveMode === 'opening_stock') {
      if (!isQtyValid) {
        toast.error('Please enter a valid positive quantity');
        return;
      }
      const costNum = parseFloat(cost) || 0;

      setIsSubmitting(true);
      try {
        await addOpeningStock.mutateAsync({
          variant_id: variantId,
          quantity: parsedQty,
          unit_cost: costNum,
          reason: reason.trim() || 'Initial opening stock entry',
        });
        toast.success(`Added ${parsedQty} opening stock for ${sku}`);
        onClose();
      } catch (err: unknown) {
        console.error(err);
        toast.error(err instanceof Error ? err.message : 'Failed to add opening stock');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // adjustment_in / adjustment_out / damaged
    if (!isQtyValid) {
      toast.error('Please enter a valid positive quantity');
      return;
    }
    if (isOverRemoval) {
      toast.error(`Cannot remove more than available stock (${currentOnHand})`);
      return;
    }
    if (!reason.trim()) {
      toast.error('Reason is required for stock adjustments');
      return;
    }

    setIsSubmitting(true);
    try {
      await adjustStock.mutateAsync({
        variant_id: variantId,
        quantity: parsedQty,
        movement_type: effectiveMode,
        reason: reason.trim(),
      });
      const label = effectiveMode === 'adjustment_out' ? 'removed from' : effectiveMode === 'damaged' ? 'marked damaged for' : 'added to';
      toast.success(`${parsedQty} unit${parsedQty !== 1 ? 's' : ''} ${label} ${sku}`);
      onClose();
    } catch (err: unknown) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to record stock adjustment');
      // Do NOT close — preserve quantity and keep modal open (FR-STOCK-005)
    } finally {
      setIsSubmitting(false);
    }
  };

  const isAddActive = !showAdvanced && primaryMode === 'add';
  const isRemoveActive = !showAdvanced && primaryMode === 'remove';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId} data-field-guide-scope
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-xl">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5 text-primary" />
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 id={titleId} className="text-base font-semibold text-foreground">
                Adjust Stock — {sku}
              </h3>
              <UserGuide compact />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p id={descId} className="mb-5 text-xs text-muted-foreground">
          Product: <span className="font-medium text-foreground">{productName}</span>
          {' · '}Current Stock:{' '}
          <span className="font-bold text-foreground">{currentOnHand}</span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* ── Primary Add / Remove toggle ───────────────────────── */}
          {!showAdvanced && (
            <div>
              <p className="mb-2 text-xs font-medium text-foreground">
                What would you like to do? <span className="text-destructive">*</span>
              </p>
              <div className="grid grid-cols-2 gap-2" role="group" aria-label="Stock adjustment action">
                <button
                  type="button"
                  onClick={() => setPrimaryMode('add')}
                  aria-pressed={isAddActive}
                  className={cn(
                    'flex items-center justify-center gap-2 rounded-md border py-2.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isAddActive
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-2 ring-emerald-500/40'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )}
                >
                  <Plus className="h-4 w-4" />
                  Add Stock
                </button>
                <button
                  type="button"
                  onClick={() => setPrimaryMode('remove')}
                  aria-pressed={isRemoveActive}
                  className={cn(
                    'flex items-center justify-center gap-2 rounded-md border py-2.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isRemoveActive
                      ? 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-2 ring-amber-500/40'
                      : 'border-border bg-background text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )}
                >
                  <Minus className="h-4 w-4" />
                  Remove Stock
                </button>
              </div>
            </div>
          )}

          {/* ── Set Exact target field (advanced only) ────────────── */}
          {effectiveMode === 'set_exact' && (
            <div className="space-y-1.5">
              <label htmlFor="stock-target-input" className="text-xs font-medium text-foreground">
                Set Stock To (Exact Quantity) <span className="text-destructive">*</span>
              </label>
              <Input
                id="stock-target-input"
                type="number"
                min="0"
                value={targetStock}
                onChange={(e) => setTargetStock(e.target.value)}
                placeholder="e.g. 20"
                required
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                helpLabel="Set Stock To (Exact Quantity) *"
              />
            </div>
          )}

          {/* ── Quantity field (all non-set_exact modes) ──────────── */}
          {effectiveMode !== 'set_exact' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label htmlFor="stock-qty-input" className="text-xs font-medium text-foreground">
                  Quantity <span className="text-destructive">*</span>
                </label>
                <Input
                  id="stock-qty-input"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="e.g. 10"
                  required
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Quantity *"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="stock-cost-input" className="text-xs font-medium text-foreground">
                  Unit Cost (₹)
                </label>
                <Input
                  id="stock-cost-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  placeholder="0.00"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  helpLabel="Unit Cost (₹)"
                />
              </div>
            </div>
          )}

          {/* ── Invalid removal warning ───────────────────────────── */}
          {isOverRemoval && (
            <p className="flex items-center gap-1.5 text-xs font-medium text-destructive" role="alert">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              Cannot remove more than available stock ({currentOnHand})
            </p>
          )}

          {/* ── Live preview card ─────────────────────────────────── */}
          {(isQtyValid || (effectiveMode === 'set_exact' && isTargetValid)) && (
            <div
              className={cn(
                'rounded-md border p-3 text-xs transition-colors',
                isDecrease
                  ? 'border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200'
                  : isIncrease
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200'
                  : 'border-border bg-muted/40 text-muted-foreground',
              )}
            >
              <div className="flex items-center justify-between font-medium">
                <span className="flex items-center gap-1.5">
                  {isDecrease && <TrendingDown className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
                  {isIncrease && <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
                  {!isDecrease && !isIncrease && <AlertCircle className="h-4 w-4 text-muted-foreground" />}
                  Stock Preview
                </span>
                {/* Human-readable calculation: 25 + 5 = 30 */}
                <span className="font-mono text-sm font-bold">
                  {currentOnHand}
                  {' '}
                  {isDecrease ? '−' : '+'}
                  {' '}
                  {Math.abs(computedDelta)}
                  {' '}
                  =
                  {' '}
                  {computedResultingStock}
                </span>
              </div>
              <p className="mt-1 text-[11px] opacity-90">
                {isNoChange
                  ? 'No stock change will occur.'
                  : isDecrease
                  ? `Stock will decrease by ${Math.abs(computedDelta)}.`
                  : `Stock will increase by ${computedDelta}.`}
              </p>
            </div>
          )}

          {/* ── Reason field ──────────────────────────────────────── */}
          <div className="space-y-1.5">
            <label htmlFor="stock-reason-input" className="text-xs font-medium text-foreground">
              Reason / Note{' '}
              {effectiveMode !== 'opening_stock' && <span className="text-destructive">*</span>}
            </label>
            <Input
              id="stock-reason-input"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                effectiveMode === 'opening_stock'
                  ? 'Initial stock entry'
                  : effectiveMode === 'damaged'
                    ? 'e.g. Damaged in transit'
                    : primaryMode === 'remove'
                      ? 'e.g. Stock discrepancy correction'
                      : 'e.g. Stock received from supplier'
              }
              required={effectiveMode !== 'opening_stock'}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              helpLabel="Reason / Note"
            />
          </div>

          {/* ── Advanced options (collapsed by default) ───────────── */}
          <div className="border-t border-border/50 pt-3">
            <button
              type="button"
              onClick={() => {
                setShowAdvanced((v) => !v);
                if (showAdvanced) setAdvancedMode(null); // reset on collapse
              }}
              className="flex w-full items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              aria-expanded={showAdvanced}
            >
              <span className="font-medium">Advanced options</span>
              {showAdvanced
                ? <ChevronUp className="h-3.5 w-3.5" />
                : <ChevronDown className="h-3.5 w-3.5" />}
            </button>

            {showAdvanced && (
              <div className="mt-3 grid grid-cols-3 gap-2" role="group" aria-label="Advanced stock action">
                {(
                  [
                    { value: 'set_exact', label: '🎯 Set Exact', description: 'Set to a specific number' },
                    { value: 'damaged', label: '🚨 Damaged / Lost', description: 'Log damaged or lost stock' },
                    { value: 'opening_stock', label: '➕ Opening Stock', description: 'Initial stock position' },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAdvancedMode(advancedMode === opt.value ? null : opt.value)}
                    aria-pressed={advancedMode === opt.value}
                    title={opt.description}
                    className={cn(
                      'flex flex-col items-center rounded-md border px-2 py-2 text-[10px] font-medium text-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      advancedMode === opt.value
                        ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/30'
                        : 'border-border bg-background text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Footer actions ────────────────────────────────────── */}
          <div className="mt-2 flex justify-end gap-2 border-t border-border pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isSubmitting ? 'Saving…' : 'Update Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
